/**
 * Slice 21a — VA Restock strip on Issues detail (Buy → tracking → Delivered / Picked up).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  useCreateInventoryReorder,
  useIssueRestockReorder,
  useUpdateInventoryReorder,
  type ReorderStatus,
} from "@/hooks/useInventoryReorders";

type ChipKey = "buy" | "transit" | "delivered" | "picked";

function chipFromStatus(status: ReorderStatus | null | undefined): ChipKey {
  if (!status || status === "pending") return "buy";
  if (status === "in_transit") return "transit";
  if (status === "delivered") return "delivered";
  return "picked";
}

const CHIP: Record<ChipKey, { label: string; className: string }> = {
  buy: { label: "Buy", className: "bg-[#fdf3e5] text-[#b45309]" },
  transit: { label: "In transit", className: "bg-[#e2eeed] text-[#15776d]" },
  delivered: { label: "Delivered", className: "bg-[#24a89a] text-white" },
  picked: { label: "Picked up", className: "bg-[#eef2f5] text-[#475569]" },
};

function truncateTracking(raw: string, max = 22): string {
  const t = raw.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

interface IssueRestockBlockProps {
  propertyId: string;
  issueReportId: string;
  itemLabel: string;
  disabled?: boolean;
}

export function IssueRestockBlock({
  propertyId,
  issueReportId,
  itemLabel,
  disabled,
}: IssueRestockBlockProps) {
  const { data: reorder, isLoading } = useIssueRestockReorder(issueReportId);
  const createReorder = useCreateInventoryReorder();
  const updateReorder = useUpdateInventoryReorder();

  const { data: buyListUrl } = useQuery({
    queryKey: ["property-buy-list-url", propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("properties")
        .select("amazon_buy_list_url")
        .eq("id", propertyId)
        .maybeSingle();
      if (error) throw error;
      return (data?.amazon_buy_list_url || "").trim() || null;
    },
    enabled: !!propertyId,
  });

  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [orderDraft, setOrderDraft] = useState("");
  const saving = createReorder.isPending || updateReorder.isPending;

  const chipKey = chipFromStatus(reorder?.status);
  const chip = CHIP[chipKey];
  const tracking = (reorder?.tracking_number || "").trim();
  const orderRef = (reorder?.order_reference || "").trim();
  const hasBuyList = Boolean(buyListUrl);

  useEffect(() => {
    if (!sheetOpen) return;
    setDraft(tracking);
    setOrderDraft(orderRef);
  }, [sheetOpen, tracking, orderRef]);

  const saveTracking = async () => {
    const value = draft.trim();
    if (!value || saving) return;
    try {
      if (reorder?.id) {
        await updateReorder.mutateAsync({
          id: reorder.id,
          propertyId,
          issueReportId,
          trackingNumber: value,
          orderReference: orderDraft.trim() || null,
        });
      } else {
        await createReorder.mutateAsync({
          propertyId,
          items: [],
          trackingNumber: value,
          orderReference: orderDraft.trim() || null,
          carrier: "amazon",
          maintenanceIssueReportId: issueReportId,
          supplierNote: itemLabel.slice(0, 200) || null,
        });
      }
      setSheetOpen(false);
      toast({ title: "Saved" });
    } catch (e) {
      toast({
        title: "Couldn’t save",
        description: e instanceof Error ? e.message : "Try again",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white py-8">
        <Loader2 className="h-5 w-5 animate-spin text-[#94a3b8]" />
      </div>
    );
  }

  const trackingLine = tracking
    ? `${truncateTracking(tracking)}${orderRef ? `  ·  Order ${truncateTracking(orderRef, 14)}` : ""}`
    : null;

  return (
    <div className="relative flex flex-col gap-3.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-5 py-[18px]">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">Restock</div>
        <span
          className={cn(
            "inline-flex items-center rounded-full px-3 py-[5px] text-[13px] font-bold whitespace-nowrap",
            chip.className,
          )}
        >
          {chip.label}
        </span>
      </div>

      {chipKey === "delivered" ? (
        <div className="rounded-[14px] bg-[#e4f5f2] px-4 py-3 text-[14px] font-semibold leading-snug text-[#17796e]">
          Delivered · cleaners will pick up and update count
        </div>
      ) : null}

      {chipKey === "picked" ? (
        <div className="text-[14px] font-medium text-[#8494a2]">Ready to close issue</div>
      ) : null}

      {trackingLine && chipKey !== "buy" ? (
        <div className="truncate font-mono text-[13px] font-medium text-[#64748b]">{trackingLine}</div>
      ) : null}

      {chipKey === "buy" ? (
        <div className="flex flex-col gap-2.5">
          {hasBuyList ? (
            <>
              <a
                href={buyListUrl!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-[52px] items-center justify-center gap-2 rounded-[14px] bg-[#24a89a] text-base font-bold text-white hover:bg-[#1b8378]"
              >
                <ExternalLink className="h-4 w-4 stroke-[2.6]" />
                Open list
              </a>
              <div className="text-center text-[13px] font-medium text-[#8494a2]">
                Opens Amazon for this property
              </div>
            </>
          ) : (
            <Link
              to={`/properties/${propertyId}?tab=settings`}
              className="inline-flex h-[52px] items-center justify-center rounded-[14px] bg-[#24a89a] px-4 text-center text-base font-bold text-white hover:bg-[#1b8378]"
            >
              Add buy list in Settings
            </Link>
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={() => setSheetOpen(true)}
            className="h-12 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white text-[15px] font-bold text-[#15776d] hover:border-[#24a89a] disabled:opacity-50"
          >
            Paste tracking
          </button>
        </div>
      ) : null}

      {chipKey === "transit" || chipKey === "delivered" ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setSheetOpen(true)}
          className="h-11 self-start rounded-[12px] px-1 text-[14px] font-bold text-[#94a3b8] hover:text-[#64748b] disabled:opacity-50"
        >
          Update tracking
        </button>
      ) : null}

      {sheetOpen ? (
        <div className="absolute inset-0 z-20 flex items-end rounded-[18px] bg-[rgba(9,14,20,0.4)]">
          <div className="flex w-full flex-col gap-4 rounded-t-[22px] bg-white px-5 pb-6 pt-4 shadow-[0_-8px_30px_rgba(15,23,32,0.12)]">
            <div className="mx-auto h-1.5 w-11 rounded-full bg-[#e6edf1]" />
            <div className="text-[22px] font-bold tracking-[-0.4px] text-[#0f1720]">Paste tracking</div>
            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-bold text-[#475569]">Tracking</label>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Number or tracking link"
                autoFocus
                className="h-[52px] w-full rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-4 text-base font-medium text-[#0f1720] outline-none placeholder:text-[#94a3b0] focus:border-[#24a89a]"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[13px] font-bold text-[#8494a2]">Order #</label>
              <input
                value={orderDraft}
                onChange={(e) => setOrderDraft(e.target.value)}
                placeholder="Optional"
                className="h-[48px] w-full rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-4 text-[15px] font-medium text-[#0f1720] outline-none placeholder:text-[#94a3b0] focus:border-[#24a89a]"
              />
            </div>
            <div className="flex gap-2.5 pt-1">
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex h-[52px] flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] text-base font-bold text-[#64748b]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!draft.trim() || saving}
                onClick={() => void saveTracking()}
                className={cn(
                  "flex h-[52px] flex-1 items-center justify-center rounded-[14px] text-base font-bold",
                  draft.trim() && !saving
                    ? "bg-[#24a89a] text-white hover:bg-[#1b8378]"
                    : "bg-[#eef2f5] text-[#94a3b0]",
                )}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default IssueRestockBlock;
